import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function FAQ({ onContactClick }) {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { key: 'whyCheap' },
    { key: 'supportRequired' },
    { key: 'whenSubscription' },
    { key: 'canCancel' },
    { key: 'whatNeeded' },
    { key: 'howLong' },
    { key: 'afterDelivery' },
    { key: 'ownership' }
  ];

  return (
    <section id="faq" className="py-24 md:py-32 bg-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <HelpCircle size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">{t('faq.badge')}</span>
          </div>
          
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="text-white">{t('faq.title')}</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
            >
              <div 
                className={`bg-slate-800/50 backdrop-blur-sm border rounded-2xl overflow-hidden transition-all duration-300 ${
                  openIndex === index 
                    ? 'border-primary/50 shadow-lg shadow-primary/10' 
                    : 'border-slate-700/50 hover:border-slate-600'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                  className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between gap-4 text-left group"
                >
                  <span className={`font-heading text-lg md:text-xl font-bold transition-colors ${
                    openIndex === index ? 'text-primary' : 'text-white group-hover:text-primary'
                  }`}>
                    {t(`faq.items.${faq.key}.question`)}
                  </span>
                  <ChevronDown 
                    className={`flex-shrink-0 transition-all duration-300 ${
                      openIndex === index 
                        ? 'rotate-180 text-primary' 
                        : 'text-slate-400 group-hover:text-primary'
                    }`}
                    size={24}
                  />
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 md:px-8 pb-5 md:pb-6 pt-2">
                        <p className="text-slate-300 leading-relaxed">
                          {t(`faq.items.${faq.key}.answer`)}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-2xl p-8">
            <h3 className="font-heading text-2xl font-bold text-white mb-3">
              {t('faq.stillQuestions')}
            </h3>
            <p className="text-slate-300 mb-6">
              {t('faq.contactUs')}
            </p>
            <button
              onClick={onContactClick}
              className="inline-flex items-center justify-center px-6 py-3 bg-primary hover:bg-primary-light text-white font-medium rounded-lg transition-colors"
            >
              {t('faq.contactButton')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
